import React, { useRef } from 'react';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { avatarGenderClass, clientNameOf } from '../clientPresentation';
import type { CalendarEntry, CalendarFilter, CalendarMonth } from '../reminderCalendar';
import type { IntlLike } from '../clientPresentation';

/**
 * The month grid. Presentational by contract (spec D11) — it takes data and
 * callbacks, never ClientV2 state, so ClientV1 can render it unchanged.
 */
export type ReminderCalendarProps = {
  month: CalendarMonth;
  visibleMonth: moment.Moment;
  now: moment.Moment;
  /** 'YYYY-MM-DD', or null when nothing is selected. */
  selectedDay: string | null;
  filter: CalendarFilter;
  adviserEmail?: string;
  onSelectDay: (day: string | null) => void;
  onMonth: (move: 'prev' | 'next' | 'today') => void;
  onFilter: (filter: CalendarFilter) => void;
  /** Back to the list. Optional so a host without a list (ClientV1) omits it. */
  onExit?: () => void;
  intl: IntlLike;
};

const DAY_KEY = 'YYYY-MM-DD';

/** Three fit a 51px cell at 375px; a fourth becomes the +n chip. */
const AVATAR_CAP = 3;

const FILTERS: CalendarFilter[] = ['reminder', 'birthday', 'all'];
const FILTER_LABEL: Record<CalendarFilter, string> = {
  reminder: 'clientv2-cal-filter-reminder',
  birthday: 'clientv2-cal-filter-birthday',
  all: 'clientv2-cal-filter-all'
};

/** A day is overdue when a reminder ON it is due — birthdays never are. */
const isDue = (entries: CalendarEntry[] | undefined): boolean =>
  Boolean(entries?.some(entry => entry.kind === 'reminder' && entry.mark === 'due'));

const initialOf = (client: CalendarEntry['client']): string => clientNameOf(client).trim().charAt(0) || '?';

const ReminderCalendar = (props: ReminderCalendarProps) => {
  const { month, visibleMonth, now, selectedDay, filter, adviserEmail, onSelectDay, onMonth, onFilter, onExit, intl } = props;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const message = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const todayKey = now.format(DAY_KEY);

  // Roving tabindex: one cell in the tab order at a time, arrows move focus.
  // The selected day owns it, else today, else the first of the month — so Tab
  // always lands somewhere meaningful rather than in the leading spill week.
  const focusKey =
    selectedDay ||
    (visibleMonth.isSame(now, 'month') ? todayKey : visibleMonth.clone().startOf('month').format(DAY_KEY));

  const moveFocus = (from: moment.Moment, days: number): void => {
    const target = from.clone().add(days, 'days').format(DAY_KEY);
    const node = gridRef.current?.querySelector<HTMLElement>(`[data-testid="cal-day-${target}"]`);
    if (node) node.focus();
  };

  const onArrowKey = (event: React.KeyboardEvent<HTMLDivElement>, date: moment.Moment): void => {
    const steps: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
    const step = steps[event.key];
    if (step !== undefined) {
      event.preventDefault();
      moveFocus(date, step);
      return;
    }
    if (event.key === 'PageDown' || event.key === 'PageUp') {
      event.preventDefault();
      onMonth(event.key === 'PageDown' ? 'next' : 'prev');
    }
  };

  return (
    <section className="clientv2-cal" data-testid="reminder-calendar">
      {/* The tab-row toggle is the other way out, but advisers did not find it:
          a labelled back button where their eyes already are. */}
      {onExit && (
        <button type="button" className="clientv2-cal-exit" data-testid="cal-exit" onClick={onExit}>
          <ArrowLeft size={14} aria-hidden="true" />
          {message('clientv2-calendar-back')}
        </button>
      )}
      <div className="clientv2-cal-filters" role="group">
        {FILTERS.map(option => (
          <button
            key={option}
            type="button"
            data-testid={`cal-filter-${option}`}
            aria-pressed={filter === option}
            className={`clientv2-cal-filter${filter === option ? ' is-active' : ''}`}
            onClick={() => onFilter(option)}
          >
            {message(FILTER_LABEL[option])}
          </button>
        ))}
      </div>

      <div className="clientv2-cal-head">
        <strong>{visibleMonth.format('YYYY-MM')}</strong>
        <div className="clientv2-cal-nav">
          <button type="button" data-testid="cal-today" onClick={() => onMonth('today')}>
            {message('clientv2-cal-this-month')}
          </button>
          <button
            type="button"
            data-testid="cal-prev"
            aria-label={message('clientv2-cal-prev-month')}
            onClick={() => onMonth('prev')}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            data-testid="cal-next"
            aria-label={message('clientv2-cal-next-month')}
            onClick={() => onMonth('next')}
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="clientv2-cal-dow" aria-hidden="true">
        {moment.weekdaysMin(true).map(day => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="clientv2-cal-grid" role="grid" ref={gridRef}>
        {month.cells.map(({ date, inMonth }) => {
          const key = date.format(DAY_KEY);
          const entries = inMonth ? month.byDay.get(key) : undefined;
          // Over the cap one avatar slot yields to the chip, so four entries
          // read as two avatars and +2 rather than three and +1.
          const shown = entries ? entries.slice(0, entries.length > AVATAR_CAP ? AVATAR_CAP - 1 : AVATAR_CAP) : [];
          const hidden = (entries?.length || 0) - shown.length;
          const selected = selectedDay === key;
          const due = isDue(entries);
          const label = entries?.length
            ? `${message('clientv2-cal-day-label', {
                date: date.format('LL'),
                count: entries.length,
                names: entries.map(entry => clientNameOf(entry.client)).join('、')
              })}${due ? `, ${message('clientv2-cal-overdue')}` : ''}`
            : message('clientv2-cal-day-label-empty', { date: date.format('LL') });

          return (
            <div
              key={key}
              role="gridcell"
              data-testid={`cal-day-${key}`}
              data-due={due ? 'true' : undefined}
              data-today={key === todayKey ? 'true' : undefined}
              aria-selected={selected}
              aria-label={inMonth ? label : undefined}
              aria-disabled={inMonth ? undefined : true}
              tabIndex={inMonth && key === focusKey ? 0 : -1}
              className={`clientv2-cal-day${inMonth ? '' : ' is-out'}${selected ? ' is-selected' : ''}${
                due ? ' is-due' : ''
              }${key === todayKey ? ' is-today' : ''}`}
              onClick={() => inMonth && onSelectDay(selected ? null : key)}
              onKeyDown={event => {
                if (!inMonth) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectDay(selected ? null : key);
                  return;
                }
                onArrowKey(event, date);
              }}
            >
              <span className="clientv2-cal-num">{date.date()}</span>
              {shown.length > 0 && (
                <span className="clientv2-cal-avatars">
                  {shown.map(entry => (
                    <span
                      key={`${entry.kind}-${entry.client._id}`}
                      data-testid="cal-avatar"
                      aria-hidden="true"
                      className={`clientv2-cal-avatar clientv2-av-${avatarGenderClass(entry.client, adviserEmail)}`}
                    >
                      {initialOf(entry.client)}
                    </span>
                  ))}
                  {hidden > 0 && (
                    <span className="clientv2-cal-avatar is-more" aria-hidden="true">
                      +{hidden}
                    </span>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="clientv2-cal-legend">
        <span>{message('clientv2-cal-legend')}</span>
        <strong>
          {message('clientv2-cal-summary', {
            reminders: month.reminderCount,
            birthdays: month.birthdayCount
          })}
        </strong>
      </div>
    </section>
  );
};

export default injectIntl(ReminderCalendar);
